/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ButtonProps, SelectFieldProps } from '@coursera/cds-core';
import { Button, SelectField, SelectOption, Typography2, breakpoints } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import styles from 'bundles/enterprise-legacy-learner-home/components/InformationCard/styles';

import _t from 'i18n!nls/program-skills-objectives';

import type { FilterDropdownOptionsMap } from './generateFilterDropdownOptions';
import type { FilterKeys, FilterState } from './useProductFilterState';
import { filterKeys } from './useProductFilterState';

const TrackedSelect = withSingleTracked({ type: 'CHECKBOX' })<SelectFieldProps>(SelectField);
const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<'button'>>(Button);

type Props = {
  dropdownOptions: FilterDropdownOptionsMap;
  filterValues: FilterState;
  onFilterChange: (key: FilterKeys, value: string) => void;
  onResetClick: () => void;
};

const stylesMap = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  selectField: css`
    min-width: 180px;

    .cds-select-field-root {
      margin-top: var(--cds-spacing-50);
    }
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  selectRow: css`
    display: flex;
    flex-direction: row;
    margin-top: var(--cds-spacing-100);
    flex-wrap: wrap;
    row-gap: var(--cds-spacing-150);
    column-gap: var(--cds-spacing-100);
  `,
  resetButton: css`
    align-self: flex-end;
  `,
  header: css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
};

const ProductFilters = ({ dropdownOptions, filterValues, onFilterChange, onResetClick }: Props) => {
  const isAnyFilterActive = Object.values(filterValues).some((value: string) => value !== '');

  return (
    <ErrorBoundaryWithTagging componentName="ProductFilters">
      <div
        // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
        css={styles.container}
      >
        <Typography2 variant="subtitleMedium" color="highlightBlue" css={stylesMap.header} component="h2">
          {_t('Filter by')}
        </Typography2>
        <div css={stylesMap.selectRow}>
          <TrackedSelect
            trackingName="skill_page_language_filter"
            label={_t('Language')}
            value={filterValues[filterKeys.LANGUAGE]}
            css={stylesMap.selectField}
            onChange={(event) => {
              onFilterChange(filterKeys.LANGUAGE, event.target.value as string);
            }}
          >
            {[{ value: '', label: _t('All languages') }, ...dropdownOptions[filterKeys.LANGUAGE]].map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </TrackedSelect>
          <TrackedSelect
            trackingName="skill_page_level_filter"
            label={_t('Level')}
            value={filterValues[filterKeys.LEVEL]}
            css={stylesMap.selectField}
            onChange={(event) => {
              onFilterChange(filterKeys.LEVEL, event.target.value as string);
            }}
          >
            {[
              { value: '', label: _t('All levels') },
              /**
               * Even though we generate the options in the component, we need want to order them correctly.
               * It's actually simpler to filter on predefined values than to implement any kind of ordering sort.
               */
              ...[
                { value: 'BEGINNER', label: _t('Beginner') },
                { value: 'INTERMEDIATE', label: _t('Intermediate') },
                { value: 'ADVANCED', label: _t('Advanced') },
              ].filter((option) =>
                dropdownOptions[filterKeys.LEVEL].map((generatedOption) => generatedOption.value).includes(option.value)
              ),
            ].map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </TrackedSelect>
          <TrackedSelect
            trackingName="skill_page_subject_filter"
            label={_t('Subject')}
            value={filterValues[filterKeys.SUBJECT]}
            css={stylesMap.selectField}
            onChange={(event) => {
              onFilterChange(filterKeys.SUBJECT, event.target.value as string);
            }}
          >
            {[{ value: '', label: _t('All subjects') }, ...dropdownOptions[filterKeys.SUBJECT]].map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </TrackedSelect>
          <TrackedSelect
            trackingName="skill_page_partner_filter"
            label={_t('Partner')}
            value={filterValues[filterKeys.PARTNER]}
            css={stylesMap.selectField}
            onChange={(event) => {
              onFilterChange(filterKeys.PARTNER, event.target.value as string);
            }}
          >
            {[{ value: '', label: _t('All partners') }, ...dropdownOptions[filterKeys.PARTNER]].map((option) => (
              <SelectOption key={option.value} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </TrackedSelect>
          <TrackedSelect
            trackingName="skill_page_learning_product_filter"
            label={_t('Learning product')}
            value={filterValues[filterKeys.LEARNING_PRODUCT]}
            css={stylesMap.selectField}
            onChange={(event) => {
              onFilterChange(filterKeys.LEARNING_PRODUCT, event.target.value as string);
            }}
          >
            {[{ value: '', label: _t('All learning products') }, ...dropdownOptions[filterKeys.LEARNING_PRODUCT]].map(
              (option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              )
            )}
          </TrackedSelect>
          <TrackedButton
            {...Button.defaultProps}
            trackingName="skill_page_reset_filter_button"
            variant="ghost"
            onClick={onResetClick}
            css={stylesMap.resetButton}
            disabled={!isAnyFilterActive}
          >
            {_t('Reset')}
          </TrackedButton>
        </div>
      </div>
    </ErrorBoundaryWithTagging>
  );
};

export default ProductFilters;
